import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSiteSettings } from '@/hooks/useSettings';
import { MapPin, Phone, Mail, Facebook, Twitter, Linkedin, Instagram, Youtube } from 'lucide-react';
import { Link } from 'react-router-dom';
import UnifiedLogo from '@/components/logo/UnifiedLogo';

const Footer = () => {
  const { language } = useLanguage();
  const siteSettings = useSiteSettings();

  const quickLinks = [
    { href: '/', label_ar: 'الرئيسية', label_en: 'Home' },
    { href: '/about', label_ar: 'عن المجموعة', label_en: 'About Group' },
    { href: '/sectors', label_ar: 'القطاعات', label_en: 'Sectors' },
    { href: '/companies', label_ar: 'الشركات', label_en: 'Companies' },
    { href: '/projects', label_ar: 'المشاريع', label_en: 'Projects' },
    { href: '/contact', label_ar: 'اتصل بنا', label_en: 'Contact Us' },
  ];

  return (
    <footer className="bg-gradient-to-b from-primary via-primary-dark to-black text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container mx-auto px-6 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3">
              <UnifiedLogo variant="light" position="footer" size="medium" />
            </div>
            
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold mb-4 text-white ${
                language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'
              }`}>
                {siteSettings.companyName(language)}
              </h3>
              <p className={`text-gray-300 mb-6 leading-relaxed ${
                language === 'ar' ? 'font-arabic-body' : 'font-english-body'
              }`}>
                {siteSettings.companyDescription(language) || (language === 'ar' 
                  ? 'مجموعة أباسك رائدة في المملكة العربية السعودية تعمل في قطاعات متنوعة'
                  : 'ABASC HOLDING is a leading company in Saudi Arabia operating in diverse sectors'
                )}
              </p>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6"
          >
            <h3 className={`text-lg font-semibold text-white ${
              language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'
            }`}>
              {language === 'ar' ? 'روابط سريعة' : 'Quick Links'}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className={`text-gray-300 hover:text-white transition-colors duration-300 flex items-center gap-2 ${
                      language === 'ar' ? 'font-arabic-body' : 'font-english-body'
                    }`}
                  >
                    <span className="text-primary">▸</span>
                    {language === 'ar' ? link.label_ar : link.label_en}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <h3 className={`text-lg font-semibold text-white ${
              language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'
            }`}>
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </h3>
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 text-white flex-shrink-0" />
                <span className={`${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
                  {siteSettings.contactEmail}
                </span>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 text-white flex-shrink-0" />
                <span className={`${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
                  {siteSettings.contactPhone}
                </span>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors"
              >
                <MapPin className="w-4 h-4 text-white flex-shrink-0 mt-1" />
                <span className={`${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
                  {siteSettings.contactAddress(language)}
                </span>
              </motion.div>
            </div>
          </motion.div>

          {/* Social Media */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            <h3 className={`text-lg font-semibold text-white ${
              language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'
            }`}>
              {language === 'ar' ? 'تابعونا' : 'Follow Us'}
            </h3>
            <div className="space-y-4">
              <p className={`text-gray-300 ${
                language === 'ar' ? 'font-arabic-body' : 'font-english-body'
              }`}>
                {language === 'ar' 
                  ? 'تابع آخر أخبارنا وتحديثاتنا على وسائل التواصل الاجتماعي'
                  : 'Follow our latest news and updates on social media'
                }
              </p>
              
              <div className="flex gap-4">
                {[
                  { icon: Facebook, url: siteSettings.socialLinks.facebook, name: 'Facebook' },
                  { icon: Twitter, url: siteSettings.socialLinks.twitter, name: 'Twitter' },
                  { icon: Linkedin, url: siteSettings.socialLinks.linkedin, name: 'LinkedIn' },
                  { icon: Instagram, url: siteSettings.socialLinks.instagram, name: 'Instagram' },
                  { icon: Youtube, url: siteSettings.socialLinks.youtube, name: 'YouTube' }
                ].filter(social => social.url).map((social, index) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.1 }}
                    className="w-10 h-10 bg-white/10 hover:bg-primary/20 rounded-lg flex items-center justify-center transition-all duration-300 group"
                    aria-label={social.name}
                  >
                    <social.icon className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer Bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 pt-8 border-t border-white/20"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className={`text-gray-300 text-center ${language === 'ar' ? 'md:text-right' : 'md:text-left'} ${
              language === 'ar' ? 'font-arabic-body' : 'font-english-body'
            }`}>
              {language === 'ar' 
                ? `© ${new Date().getFullYear()} مجموعة أباسك. جميع الحقوق محفوظة.`
                : `© ${new Date().getFullYear()} ABASC HOLDING. All rights reserved.`
              }
            </p>
            
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className={`text-gray-300 hover:text-white transition-colors duration-300 ${
                  language === 'ar' ? 'font-arabic-body' : 'font-english-body'
                }`}
              >
                {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
              </Link>
              <Link
                to="/terms"
                className={`text-gray-300 hover:text-white transition-colors duration-300 ${
                  language === 'ar' ? 'font-arabic-body' : 'font-english-body'
                }`}
              >
                {language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;